import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSortModule, Sort } from '@angular/material/sort';
import { Subject, takeUntil } from 'rxjs';
import { IPagination } from 'app/common/interfaces';
import { LiquidationAttributesReport, LiquidationDetail } from '../../monitor/monitor-routes/interfaces/liquidation.interface';
import { LiquidationService } from '../../monitor/monitor-routes/liquidation.service';
import { DateHelper } from 'app/utils/date.helper';
import { IFilters } from '../../filters/interfaces/filterInterface';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { MonedaPipe } from 'app/pipes/moneda.pipe';
import { FormatOtherDateHourPipe } from 'app/pipes/other-date.pipe';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { RoutesFiltersComponent } from '../../filters/routes-filters/routes-filters.component';
import { DatesFiltersComponent } from '../../filters/dates-filters/dates-filters.component';
import { ConfigurationFiltersComponent } from '../../filters/configuration-filters/configuration-filters.component';
import { BusinessPartnersFiltersComponent } from '../../filters/business-partners-filters/business-partners-filters.component';
import { LiquidationReportExport } from '../../assistant/delivery/loads/exports/reports/liquidation-report.export';
import { LIQUIDATION_EXPORT_ID, LIQUIDATION_PROPERTIES, LIQUIDATION_TITLE } from '../../assistant/delivery/loads/exports/reports/liquidation-report.constants';
import { ExportDialogComponent, ExportOptions } from 'app/layout/export-overlay';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { MatDialog } from '@angular/material/dialog';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { MatMenuModule } from "@angular/material/menu";
import { MatSnackBar } from '@angular/material/snack-bar';
import { getCode } from 'app/utils/method';
import { Router } from '@angular/router';
import { CompanyService } from '../../configuration/company/company.service.service';
import { CompanyAttributes } from '@albatrosdeveloper/ave-models-package';
import { BatchesDetailComponent } from 'app/modules/shared/batches-detail/batches-detail.component';
import { SmartGridComponent, ColDef, GridConfig, PageChangedEvent, SortChangedEvent } from 'app/shared/components/smart-grid';
import { TripLiquidationCellRendererComponent } from './cell-renderers/trip-liquidation-cell-renderer.component';
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';
import { ManualLabelSelectionIndicatorComponent } from 'app/shared/components/manual-label-selection-indicator';

@Component({
  selector: 'app-liquidations',
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    MonedaPipe,
    NgSelectComponent,
    MatTooltipModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    FilterComponent,
    RoutesFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    BusinessPartnersFiltersComponent,
    HoverMenuDirective,
    MatMenuModule,
    SmartGridComponent,
    TripLiquidationCellRendererComponent,
    ManualLabelSelectionIndicatorComponent
  ],
  templateUrl: './liquidations.component.html',
  styleUrl: './liquidations.component.scss',
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    LiquidationReportExport
  ]
})
export class LiquidationsComponent implements OnInit, OnDestroy {
  @ViewChild("rowDetailsTemplate", { static: false })
  rowDetailsTemplate!: TemplateRef<any>;

  getCode = getCode;

  private destroy$ = new Subject<void>();

  // SmartGrid data
  liquidations = signal<LiquidationAttributesReport[]>([]);
  totalItems = signal<number>(0);
  isLoading = signal<boolean>(false);

  // SmartGrid configuration
  columnDefs: ColDef<LiquidationAttributesReport>[] = [];
  gridConfig: GridConfig<LiquidationAttributesReport> = {} as GridConfig<LiquidationAttributesReport>;
  displayedColumns: string[] = [
    'det',
    'trip',
    'subRoute',
    'vehicle',
    'code',
    'user',
    'company',
    'salesOffice',
    'createdAt',
  ];

  dataSource = new MatTableDataSource<LiquidationAttributesReport>([]);

  // Row expansion state
  expandedLiquidation: LiquidationAttributesReport | null = null;

  // Detail table (Material Table for nested detail)
  materialDetailsDataSource = new MatTableDataSource<LiquidationDetail>([]);
  materialDetailColumns: string[] = [
    'material',
    'loaded',
    'delivered',
    'returnedBE',
    'returnedME',
    'missing',
  ];

  companyList: CompanyAttributes[] = [];
  loadConfigData: boolean = false;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions = [20, 50, 100];

  form: FormGroup;

  UMPACTIVE: boolean = false;

  constructor(
    private liquidationService: LiquidationService,
    private fb: FormBuilder,
    private searchService: SearchService,
    private reportsRouteExport: LiquidationReportExport,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly companyService: CompanyService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.initializeForm();
  }

  ngOnInit(): void {
    this.initializeColumnDefinitions();
    this.initializeGridConfig();
    this.setupSearchService();
    this.loadLiquidations();
    this.fetchCompanies();
    this.form.get('companyId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyId: number) => {
        if (companyId && this.companyList.length > 0) {
          const selectedCompany: CompanyAttributes | undefined = this.companyList.find(
            (company: CompanyAttributes) => company.id === companyId
          );
          if (selectedCompany) {
            this.setUMPConfiguration(selectedCompany);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  private initializeForm(): FormGroup {
    const today = new Date();
    return this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      end_startTrip: [],
      end_endTrip: [],
      companyId: [],
      salesOfficeId: [],
      assignedUserId: [],
      searchAssignedUser: [],
    });
  }

  goToTravel(element: LiquidationAttributesReport) {
    if (element.tripId) {
      const queryParams = { tripId: element.tripId };
      const urlTree = this.router.createUrlTree(['monitor', 'principal-screen'], { queryParams });
      const absoluteUrl = window.location.origin + this.router.serializeUrl(urlTree);
      window.open(absoluteUrl, '_blank');
    }
  }

  private setupSearchService(): void {
    this.searchService.searchTerm$
      .pipe(takeUntil(this.destroy$))
      .subscribe(term => {
        this.currentPage = 1;
        this.loadLiquidations();
      });
  }

  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe({
      next: (res) => {
        this.companyList = res.data;
        if (this.companyList.length === 1) {
          this.form.get("companyId")?.setValue(this.companyList[0].id);
          this.setUMPConfiguration(this.companyList[0]);
          this.handleFilterSearch();
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener las compañías.",
          "Cerrar",
          { duration: 5000 }
        );
      }
    });
  }

  private setUMPConfiguration(company: CompanyAttributes): void {
    this.UMPACTIVE = company.UMPActive || false;
  }

  onCompanyChange(): void {
    const companyId: number = this.form.get('companyId')?.value;
    if (companyId) {
      const selectedCompany: CompanyAttributes | undefined = this.companyList.find(
        (company: CompanyAttributes) => company.id === companyId
      );
      if (selectedCompany) {
        this.setUMPConfiguration(selectedCompany);
      }
    }
    this.handleFilterSearch();
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
    if (this.companyList.length === 0) {
      this.fetchCompanies();
    }
  }



  private async loadLiquidations(): Promise<void> {
    this.isLoading.set(true);

    const search = this.searchService.searchTermSync;

    const filters = this.buildFilters();
    const options: IPagination<LiquidationAttributesReport> = {
      currentPage: this.currentPage,
      limitPerPage: this.pageSize,
      search,
      sort: 'createdAt',
      sortDirection: 'DESC'
    };

    this.liquidationService
      .getAllLiquidationsWithFilters(options, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const liquidations = response.data?.data || [];
          const total = response.data?.pageInfo?.[0]?.total || 0;

          this.liquidations.set(liquidations);
          this.totalItems.set(total);

          // Auto-expand first row on initial load (optional)
          if (!this.expandedLiquidation && liquidations.length > 0) {
            this.expandedLiquidation = liquidations[0];
            this.loadMaterialDetails(liquidations[0]);
          }

          this.isLoading.set(false);
        },
        error: (error) => {
          this.snackBar.open(
            'Error al cargar las liquidaciones',
            'Cerrar',
            { duration: 5000 }
          );
          this.isLoading.set(false);
        }
      });
  }


  private buildFilters(): IFilters {
    return {
      start: DateHelper.getStartOfDay(this.form.get('start')?.value),
      end: DateHelper.getEndOfDay(this.form.get('end')?.value) ?? DateHelper.getEndOfDay(this.form.get('start')?.value) ?? null,
      startTrip: DateHelper.getStartOfDay(this.form.get('startTrip')?.value),
      endTrip: DateHelper.getEndOfDay(this.form.get('endTrip')?.value) ?? DateHelper.getEndOfDay(this.form.get('startTrip')?.value) ?? null,
      end_startTrip: DateHelper.getStartOfDay(this.form.get('end_startTrip')?.value),
      end_endTrip: DateHelper.getEndOfDay(this.form.get('end_endTrip')?.value) ?? DateHelper.getEndOfDay(this.form.get('end_startTrip')?.value) ?? null,
      companyId: this.form.get('companyId')?.value,
      salesOfficeId: this.form.get('salesOfficeId')?.value,
      assignedUserId: this.form.get('assignedUserId')?.value
    };
  }


  handleFilterSearch(): void {
    this.currentPage = 1;
    this.loadLiquidations();
  }

  clearFilters(): void {
    const today = new Date();
    this.form.reset({
      start: today,
      end: today
    });
    this.handleFilterSearch();
  }

  onPageChange(event: PageChangedEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadLiquidations();
  }

  onSortChange(event: SortChangedEvent<LiquidationAttributesReport>): void {
    this.currentPage = 1;
    this.loadLiquidations();
  }

  onRowExpansionChanged(event: {
    data: LiquidationAttributesReport;
    expanded: boolean;
    expandedRows: LiquidationAttributesReport[];
  }): void {
    const liquidation = event.data;

    if (!event.expanded) {
      // Row collapsed - clear detail data
      this.expandedLiquidation = null;
      this.materialDetailsDataSource.data = [];
      this.cdr.detectChanges();
      return;
    }

    // Row expanded - load detail data
    this.expandedLiquidation = liquidation;
    this.loadMaterialDetails(liquidation);
    this.cdr.detectChanges();
  }

  onExportButtonPressed(): void {
    const search = this.searchService.searchTermSync;

    const filters = this.buildFilters();
    if (this.totalItems() === 0) {
      this.snackBar.open(
        'No hay existen datos disponibles para exportar',
        'Cerrar',
        { duration: 3000 }
      );
      return;
    }
    const optionsFilters: IPagination<LiquidationAttributesReport> = {
      currentPage: this.currentPage,
      limitPerPage: this.pageSize,
      search,
      sort: 'createdAt',
      sortDirection: 'DESC'
    };

    if (!this.reportsRouteExport.canStartTask(LIQUIDATION_EXPORT_ID)) return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: LIQUIDATION_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.reportsRouteExport.export(
          options.format,
          options.range,
          this.totalItems(),
          this.pageSize,
          this.currentPage,
          LIQUIDATION_EXPORT_ID,
          LIQUIDATION_TITLE,
          LIQUIDATION_PROPERTIES,
          this.UMPACTIVE,
          filters,
          optionsFilters
        );
      }
    });
  }

  openBatchesDetail(batches, material, baseQuantity, pararellQuantity, serial) {
      const detail = {
        material: material, 
        batches: batches,
      };

      const orderModal = {
        baseQuantity: baseQuantity,
        pararellQuantity: pararellQuantity,
        serial: serial ?? 'Sin serial'
      }
  
      const dialogRef = this.dialog.open(BatchesDetailComponent, {
        width: "90%",
        data: {
          order: orderModal,
          data: detail,
          type: "normal",
          isEdit: false,
          isChangeCostBatch: false,
        },
        disableClose: true,
      });
    }

  private loadMaterialDetails(liquidation: LiquidationAttributesReport): void {
    this.materialDetailsDataSource.data = liquidation.detail || [];
  }


  getStatusDisplay(liquidation: LiquidationAttributesReport): {
    text: string;
    class: string;
  } {
    if (liquidation.statusERP === OrderStatusERPEnum.ORDER_CREATED) {
      return {
        text: 'Sincronizado',
        class: 'status-synchronized',
      };
    } else if (liquidation.statusERP === OrderStatusERPEnum.ORDER_PENDING) {
      return {
        text: 'Pendiente',
        class: 'status-pending',
      };
    } else {
      return {
        text: 'En Proceso',
        class: 'status-processing',
      };
    }
  }


  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private initializeColumnDefinitions(): void {
    this.columnDefs = [
      // Trip Column
      {
        field: 'tripId',
        headerName: 'Viaje',
        cellRenderer: TripLiquidationCellRendererComponent,
        minWidth: 150,
        sortable: false,
      },
      // Vehicle Column
      {
        field: 'vehicle.name' as any,
        headerName: 'Vehiculo',
        valueFormatter: (value, data) => data?.vehicle?.name ?? '-',
        minWidth: 150,
        sortable: true,
        filterOptions: {
          valueExtractor: (item: any) => item?.vehicle?.name ?? '-',
          customFilterFn: (rowData: any, filterValue: string) => {
            const vehicleName = rowData?.vehicle?.name ?? '-';
            if (!vehicleName || vehicleName === '-') return !filterValue;
            return vehicleName === filterValue;
          },
        },
      },
      // Document Serial Column
      {
        field: 'serial',
        headerName: 'No. Documento',
        valueFormatter: (value, data) => data?.serial ?? '-',
        cellClass: 'font-bold text-xs',
        minWidth: 140,
        sortable: true,
      },
      // User Column
      {
        field: 'tripUser.user_login.username' as any,
        headerName: 'Usuario',
        valueFormatter: (value, data) => data?.tripUser?.user_login?.username ?? '-',
        cellClass: 'font-bold text-xs',
        minWidth: 120,
        sortable: false,
        filterOptions: {
          valueExtractor: (item: any) => item?.tripUser?.user_login?.username ?? '-',
          customFilterFn: (rowData: any, filterValue: string) => {
            const username = rowData?.tripUser?.user_login?.username ?? '-';
            if (!username || username === '-') return !filterValue;
            return username === filterValue;
          },
        },
      },
      // Company Column
      {
        field: 'company.name' as any,
        headerName: 'Compañia',
        valueFormatter: (value, data) => data?.company?.name ?? '-',
        cellClass: 'font-bold text-xs',
        minWidth: 150,
        sortable: true,
        filterOptions: {
          valueExtractor: (item: any) => item?.company?.name ?? '-',
          customFilterFn: (rowData: any, filterValue: string) => {
            const companyName = rowData?.company?.name ?? '-';
            if (!companyName || companyName === '-') return !filterValue;
            return companyName === filterValue;
          },
        },
      },
      // Sales Office Column
      {
        field: 'salesOffice.name' as any,
        headerName: 'Oficina de Venta',
        valueFormatter: (value, data) => data?.salesOffice?.name ?? '-',
        cellClass: 'font-bold text-xs',
        minWidth: 180,
        sortable: true,
        filterOptions: {
          valueExtractor: (item: any) => item?.salesOffice?.name ?? '-',
          customFilterFn: (rowData: any, filterValue: string) => {
            const salesOfficeName = rowData?.salesOffice?.name ?? '-';
            if (!salesOfficeName || salesOfficeName === '-') return !filterValue;
            return salesOfficeName === filterValue;
          },
        },
      },
      // Created At Column
      {
        field: 'createdAt',
        headerName: 'Fecha de Creación',
        valueFormatter: (value, data) => this.formatDate(data?.createdAt),
        minWidth: 180,
        sortable: true,
        filterOptions: {
          valueExtractor: (item: any) => {
            if (!item.createdAt) return '-';
            const date = new Date(item.createdAt);
            return date.toLocaleDateString('es-ES');
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            if (!rowData.createdAt) return !filterValue;
            const date = new Date(rowData.createdAt);
            const formattedDate = date.toLocaleDateString('es-ES');
            return formattedDate === filterValue;
          },
        },
      },
    ];
  }

  private initializeGridConfig(): void {
     this.gridConfig = {
       rowIdField: "_id" as any,
       emptyState: {
         message: "No encontramos tus resultados!",
         showIcon: true,
       },
       pagination: {
         enabled: true,
         mode: "api",
         pageSize: 50,
         pageSizeOptions: [50, 100, 150, 200],
         showPaginator: true,
       },
       sorting: {
         enabled: true,
         mode: "api",
         multiColumn: false,
         defaultSort: [{ field: "total" as any, direction: "desc" }],
       },
       search: {
         enabled: false,
       },
       columnFilters: {
         enabled: true,
         mode: "local",
       },
       columnVisibility: {
         enabled: false, // Disabled - now in settings panel
       },
       selection: {
         enabled: false,
       },
       autoSizing: {
         enabled: true,
         includeHeaders: true,
         skipHidden: true,
         padding: 40, // Increased for more spacing
       },
       styling: {
         maxHeight: "85vh",
         minHeight: "80vh",
         density: "comfortable",
         fontSize: "12px",
         cellPaddingX: "3px",
         cellPaddingY: "3px",
       },
       settingsPanel: {
         enabled: true,
         position: "right",
         enableColumnVisibility: true,
         defaultDensity: "compact",
       },
     };
  }

}